<template>
  <nuxt-link
    :to="`/${game.locale}/${game.slug}`"
    :aria-label="game.name"
    class="group relative block h-full shrink-0 snap-start rounded-xl ring-2 ring-transparent transition-all duration-300 xl:hover:ring-brand xl:hover:scale-105"
    data-testid="game-server-card"
    @click.capture="guardDragClick"
  >
    <!-- Background image -->
    <div class="absolute inset-0 overflow-hidden rounded-xl">
      <img
        v-if="coverUrl"
        :src="coverUrl"
        :alt="game.topCoverImage?.alternativeText || game.name"
        loading="lazy"
        decoding="async"
        draggable="false"
        class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
      />
      <img
        v-if="overlayUrl"
        :src="overlayUrl"
        :alt="''"
        aria-hidden="true"
        draggable="false"
        class="absolute inset-0 h-full w-full object-cover"
      />
    </div>

    <!-- Discount badge -->
    <span
      v-if="isDiscounted"
      class="absolute right-0 top-[15%] rounded-s bg-danger px-2 py-1 font-bold leading-normal text-mouse"
    >
      -{{ discountPercentage }}%
    </span>

    <!-- Bottom info overlay -->
    <div
      class="absolute inset-x-0 bottom-0 flex h-24 flex-col items-center justify-center gap-1 rounded-b-xl bg-onyx/80 px-4 backdrop-blur-xs"
    >
      <span class="block max-w-full truncate font-bold text-mouse">
        {{ game.name }}
      </span>

      <!-- Price (default) -->
      <span
        v-if="hasPricing"
        class="block whitespace-nowrap text-mouse xl:group-hover:hidden"
        data-testid="game-card-price"
      >
        <i18n-t v-if="isDiscounted" keypath="landingpage/price_starting">
          <template #days>{{ game.startConfiguration!.duration }}</template>
          <template #price>
            <s class="text-rhino">{{ startingPrice }}</s>
            <strong class="ml-1">{{ discountedPrice }}</strong>
          </template>
        </i18n-t>
        <template v-else>
          {{ t('landingpage/price_from', { price: startingPrice }) }}
        </template>
      </span>

      <!-- Order button (hover) -->
      <span
        v-if="hasPricing"
        class="hidden w-full rounded-md bg-brand px-3 py-1.5 text-center text-onyx font-bold uppercase tracking-wider xl:group-hover:block"
      >
        {{
          isPreOrder
            ? t('frontend/preorder-now')
            : t('landingpage/index/order_now')
        }}
      </span>
    </div>
  </nuxt-link>
</template>

<script setup lang="ts">
import type { StrapiProduct } from '~/apollo/types/types';
import { getLargestImageURL } from '~/helpers/strapiHelper';

const { t } = useI18n();
const { formatStrapiPrice } = useNumberMoneyFormatting();

const props = defineProps<{
  game: StrapiProduct;
}>();

const isDragging = inject('isDragging', ref(false));

const coverUrl = computed(() =>
  props.game.topCoverImage
    ? getLargestImageURL(props.game.topCoverImage)
    : null,
);
const overlayUrl = computed(
  () => props.game.topCoverImage?.overlay?.url ?? null,
);
const isPreOrder = computed(() => timeIsAfterNow(props.game.releaseAt));
const hasPricing = computed(
  () => (props.game.startConfiguration?.prices?.length ?? 0) > 0,
);
const startingPrice = computed(() =>
  hasPricing.value
    ? formatStrapiPrice(props.game.startConfiguration!.prices[0])
    : '',
);
const isDiscounted = computed(() => props.game.isDiscountedGame);
const discountPercentage = computed(() =>
  Number(props.game.discountedPercentage ?? 0),
);
const discountedPrice = computed(() =>
  hasPricing.value
    ? formatStrapiPrice(
        props.game.startConfiguration!.prices[0],
        (props.game.discountedPercentage ?? 0) / 100,
      )
    : '',
);

function guardDragClick(e: MouseEvent) {
  if (isDragging.value) {
    e.preventDefault();
    e.stopPropagation();
  }
}
</script>
